import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { EventsService } from 'src/app/services/events.service';
import { FileService } from 'src/app/services/file.service';
import { LoadingService } from 'src/app/services/loading.service';
import { PlatformService } from 'src/app/services/platform.service';
import { SurveyService } from 'src/app/services/survey.service';

@Component({
  selector: 'page-full-screen-survey',
  templateUrl: 'full-screen-survey.html',
  styleUrls: ['full-screen-survey.scss']
})
export class FullScreenSurveyPage {
  @ViewChildren('fileImage') fileImages: QueryList<ElementRef>;
  @ViewChildren('fileDocument') fileDocuments: QueryList<ElementRef>;

  public surveyForm: UntypedFormGroup;
  public surveyInitConfig: any;
  public answeredOnce: boolean = false;
  public completedForm: boolean = false;
  public confirmationMessage: string = '';
  public isFormLoaded: boolean = false;
  public surveyFormDataResponse: any;
  public isFormInvalid: boolean = false;

  public canAnswer: boolean = false;
  public pageId;
  public survey$;
  public survey;
  public title$: any;

  public webImage$;
  public showImageContainer = false;
  public matchingImages$: BehaviorSubject<any> = new BehaviorSubject([]);
  public matchingDocuments$: BehaviorSubject<any> = new BehaviorSubject([]);

  constructor(
    public router: Router,
    public surveyProvider: SurveyService,
    private loading: LoadingService,
    private analyticsService: AnalyticsService,
    private route: ActivatedRoute,
    private events: EventsService,
    private fileService: FileService,
    private platformService: PlatformService
  ) {}

  ionViewWillEnter() {
    this.surveyForm = new UntypedFormGroup({});
    this.pageId = +this.route.snapshot.paramMap.get('id');
    this.getSurveyContent();
    this.registerPageInAnalytics();
  }

  private getSurveyContent() {
    this.survey$ = this.surveyProvider.getSurvey(this.pageId).pipe(
      tap(
        (response) => {
          this.survey = response;
          this.canAnswer = !response.answered || (response.multipleResponses && response.answered);
          this.confirmationMessage = response.confirmationMessage;
          this.surveyFormDataResponse = response.items.itemList;
          this.surveyForm = this.createForm(this.surveyFormDataResponse);
          this.isFormLoaded = true;
        },
        (error) => {
          console.error(error);
        }
      )
    );

    this.title$ = this.survey$.pipe(
      map((s: any) => s.title),
      tap((title) => this.events.pages$$.next(title))
    );
  }

  private createForm(surveyFormDataResponse) {
    let group: any = {};
    surveyFormDataResponse.forEach((question) => {
      question.response = [];
      let validators: any = Validators.maxLength(question.maxLength);
      if (question.mandatory) {
        validators = [Validators.required, Validators.maxLength(question.maxLength)];
      }

      group[question.question] = new UntypedFormControl(null, validators);
    });

    return new UntypedFormGroup(group);
  }

  public setOptionValue(option) {
    const key = Object.keys(option);
    const value = option[key[0]];
    return value;
  }

  public prepareQuestion(option) {
    option.isChecked = false;
  }

  public getTitleQuestion(option) {
    return Object.keys(option)[0];
  }

  public async onSubmit() {
    // Combinar los valores del formulario con los blobs del servicio ANTES de la validación
    const formValues = this.surveyForm.value;
    
    // Agregar blobs al formulario
    this.mergeBlobsIntoForm(formValues);

    // Verificar campos de imagen obligatorios DESPUÉS de agregar los blobs
    const hasRequiredImageErrors = this.checkRequiredImageFields();
    
    if (!this.surveyForm.valid || hasRequiredImageErrors) {
      this.checkFormKeys();
      this.isFormInvalid = true;
      return;
    }
    this.isFormInvalid = false;
    
    const userResponse = await this.surveyProvider.prepareUserResponse(formValues, this.surveyFormDataResponse);
    this.submit(userResponse);
  }

  private mergeBlobsIntoForm(formValues: any) {
    const images = this.surveyProvider.images$.value;
    const documents = this.surveyProvider.documents$.value;

    // Procesar imágenes
    images.forEach(image => {
      if (image.image?.blob) {
        this.updateFormControlWithBlob(image.questionId, image.image.blob, formValues);
      }
    });

    // Procesar documentos
    documents.forEach(document => {
      if (document.document?.blob) {
        this.updateFormControlWithBlob(document.questionId, document.document.blob, formValues);
      }
    });
  }

  private updateFormControlWithBlob(questionId: string, blob: Blob, formValues: any) {
    const question = this.surveyFormDataResponse.find(q => q.id === questionId);
    if (!question) return;

    formValues[question.question] = blob;
    
    // Actualizar el control del formulario
    const control = this.surveyForm.get(question.question);
    if (control) {
      control.setValue(blob);
      control.markAsTouched();
      control.markAsDirty();
    }
  }

  private checkRequiredImageFields(): boolean {
    let hasErrors = false;
    const images = this.surveyProvider.images$.value;
    
    this.surveyFormDataResponse.forEach((question) => {
      if (question.inputType === 'image' && question.mandatory) {
        const hasImage = images.some((image) => image.questionId === question.id);
        if (!hasImage) {
          // Marcar el control como inválido
          const control = this.surveyForm.get(question.question);
          if (control) {
            control.setErrors({ required: true });
            hasErrors = true;
          }
        }
      }
    });
    
    return hasErrors;
  }

  private async submit(responses) {
    await this.loading.show();
    this.surveyProvider
      .sendSurvey(this.pageId, responses)
      .pipe(finalize(async () => await this.loading.hide()))
      .subscribe({
        next: (res) => {
          this.completedForm = true;
          this.answeredOnce = res.answered || (res.answered && res.multipleResponses);
          this.confirmationMessage = res.confirmationMessage;

          if (this.survey.multipleResponses === true) {
            this.surveyForm.reset();
            this.surveyProvider.images$.next([]);
            this.matchingImages$.next([]);
          }

          this.registerEventInAnalytics();
        },
        error: (err) => {
          console.log(err);
        }
      });
  }

  registerEventInAnalytics() {
    const paramsToAnalytics = { actions: 0 };
    const name = 'Titulo de encuesta';
    const id = this.pageId;
    this.analyticsService.registerEvent(paramsToAnalytics, name, id);
    this.loading.hide();
  }

  registerPageInAnalytics() {
    const paramsToAnalytics = { navigation: 2 };
    const name = 'Titulo de encuesta';
    const id = this.pageId;
    this.analyticsService.registerEvent(paramsToAnalytics, name, id);
  }

  private checkFormKeys() {
    let keys = Object.keys(this.surveyForm.controls);
    keys.forEach((key) => {
      this.surveyForm.controls[key].updateValueAndValidity();
    });
  }

  public checkboxChange($event, question, option) {
    const selected = Object.keys(option)[0];

    let value = [];

    if (this.surveyForm.value[question] !== null) {
      value = [...this.surveyForm.value[question]];
    }

    if ($event.detail.checked) {
      value = [...new Set([...value, selected])];
    } else {
      value = [...new Set([...value.filter((value) => value !== selected)])];
    }

    this.surveyForm.patchValue({ [question]: value });
  }

  /**
   * function to adjust the height of the message textarea
   * @param {any} event - the event, which is provided by the textarea input
   * @return {void}
   */
  public adjustTextarea(event: any): void {
    let textarea: any = event.target;
    textarea.style.overflow = 'hidden';
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
    return;
  }

  public radioGroupChange($event, question) {
    this.surveyForm.patchValue({ [question]: $event.detail.value });
  }

  public async uploadCameraPictureWeb(event, form, question) {
    const fileInput = this.fileImages.find((fileInput) => fileInput.nativeElement.id === `fileImage_${question.id}`);
    await this.surveyProvider.uploadCameraPictureWeb(event, form, question, fileInput);
    
    // Cargar la imagen después de subirla
    await this.loadMatchingImage(question);
    
    // Limpiar errores de validación si se subió una imagen
    this.clearImageValidationErrors(question);
  }

  public async selectPicture(question) {
    const fileInput = this.fileImages.find((fileInput) => fileInput.nativeElement.id === `fileImage_${question.id}`);

    if (fileInput) {
      const result = await this.surveyProvider.selectPicture(fileInput, question, this.surveyForm);
      
      // Cargar tanto imágenes como documentos para asegurar que se muestren correctamente
      await this.loadMatchingImage(question);
      await this.loadMatchingDocument(question);
      
      // Limpiar errores de validación si se subió una imagen o documento
      this.clearImageValidationErrors(question);
      this.clearDocumentValidationErrors(question);
    }
  }

  private clearImageValidationErrors(question) {
    const control = this.surveyForm.get(question.question);
    if (control && control.hasError('required')) {
      control.setErrors(null);
    }
  }

  async loadMatchingImage(question) {
    const images = this.surveyProvider.images$.value;

    if (images && images.length > 0) {
      const matchingImage = images.find((image) => image.questionId === question.id);

      if (matchingImage) {
        let base64String;

        try {
          console.log('loadMatchingImage - isWeb:', this.platformService.isWeb());
          console.log('loadMatchingImage - isMobile:', this.platformService.isMobile());
          console.log('loadMatchingImage - platform:', this.platformService.get());
          
          // Si tiene webPath y es un blob URL, procesar como web independientemente de la plataforma
          if (matchingImage.image.webPath && matchingImage.image.webPath.startsWith('blob:')) {
            console.log('loadMatchingImage - processing blob URL as WEB');
            try {
              const response = await fetch(matchingImage.image.webPath);
              const blob = await response.blob();
              const reader = new FileReader();
              reader.onload = (e) => {
                base64String = e.target.result as string;
                
                const currentImages = this.matchingImages$.value;
                const filteredImages = currentImages.filter((img) => img.question !== question.id);
                
                this.matchingImages$.next(
                  filteredImages.concat({
                    question: question.id,
                    image: base64String
                  })
                );
                
                // Limpiar documento si existe
                this.clearDocument(question.id);
              };
              reader.readAsDataURL(blob);
              return;
            } catch (error) {
              console.error('Error processing blob URL:', error);
            }
          }
          
          if (this.platformService.isWeb()) {
            console.log('loadMatchingImage - processing as WEB');
            // En web, la imagen puede venir de diferentes formas
            if (matchingImage.image.blob) {
              // Si viene de input file
              const reader = new FileReader();
              reader.onload = (e) => {
                base64String = e.target.result as string;
                
                const currentImages = this.matchingImages$.value;
                const filteredImages = currentImages.filter((img) => img.question !== question.id);
                
                this.matchingImages$.next(
                  filteredImages.concat({
                    question: question.id,
                    image: base64String
                  })
                );
                
                // Limpiar documento si existe
                this.clearDocument(question.id);
              };
              reader.readAsDataURL(matchingImage.image.blob);
              return;
            } else if (matchingImage.image.image) {
              // Si viene de generateFile - ya es base64
              base64String = matchingImage.image.image;
            } else if (matchingImage.image.webPath) {
              // Si viene de cámara móvil o selección de galería
              try {
                const base64Image = await this.fileService.getImageBase64(matchingImage.image.webPath);
                base64String = base64Image;
              } catch (error) {
                console.error('Error getting image base64 from webPath:', error);
              }
            } else {
              return;
            }
          }

          if (this.platformService.isMobile()) {
            const pathOrWebPath = matchingImage.image.path || matchingImage.image.webPath ||
              (typeof matchingImage.image === 'string' ? matchingImage.image : null);
            if (pathOrWebPath) {
              const base64Image = await this.fileService.convertFilePathToBase64(pathOrWebPath);
              const format = (matchingImage.image && matchingImage.image.format) ? matchingImage.image.format : 'jpeg';
              base64String = `data:image/${format};base64,${base64Image}`;
            } else if (matchingImage.image.image) {
              base64String = matchingImage.image.image;
            }
          }

          if (base64String && base64String !== 'undefined') {
            const currentImages = this.matchingImages$.value;
            const filteredImages = currentImages.filter((img) => img.question !== question.id);

            this.matchingImages$.next(
              filteredImages.concat({
                question: question.id,
                image: base64String
              })
            );

            // Limpiar documento si existe
            this.clearDocument(question.id);
          } else {
            console.error('loadMatchingImage - base64String is undefined or empty');
          }
        } catch (error) {
          console.error('Error loading image:', error);
        }

        return;
      }
    }
    return './assets/imgs/photo_placeholder.svg';
  }

  getImageSrc(questionId: string) {
    // Si hay un documento seleccionado, mostrar el placeholder
    if (this.getDocumentName(questionId)) {
      return './assets/imgs/photo_placeholder.svg';
    }
    
    const images = this.matchingImages$.value;
    const matchingImage = images.find((image) => image.question === questionId);
    if (!matchingImage) {
      return './assets/imgs/photo_placeholder.svg';
    }

    return matchingImage.image;
  }

  public async uploadDocumentWeb(event, form, question) {
    const fileInput = this.fileDocuments.find((fileInput) => fileInput.nativeElement.id === `fileDocument_${question.id}`);
    await this.surveyProvider.uploadDocumentWeb(event, form, question, fileInput);
    
    // Cargar el documento después de subirlo
    await this.loadMatchingDocument(question);
    
    // Limpiar errores de validación si se subió un documento
    this.clearDocumentValidationErrors(question);
  }

  public async selectDocument(question) {
    console.log('FullScreenSurveyPage.selectDocument() called for question:', question);
    const fileInput = this.fileDocuments.find((fileInput) => fileInput.nativeElement.id === `fileDocument_${question.id}`);
    console.log('Found fileInput:', fileInput);

    if (fileInput) {
      console.log('Calling surveyProvider.selectDocument()');
      await this.surveyProvider.selectDocument(fileInput, question, this.surveyForm);
      
      // Cargar tanto imágenes como documentos para asegurar que se muestren correctamente
      await this.loadMatchingImage(question);
      await this.loadMatchingDocument(question);
      

      
      // Limpiar errores de validación si se subió una imagen o documento
      this.clearImageValidationErrors(question);
      this.clearDocumentValidationErrors(question);
    } else {
      console.log('No fileInput found for question:', question.id);
    }
  }

  private clearDocumentValidationErrors(question) {
    const control = this.surveyForm.get(question.question);
    if (control && control.hasError('required')) {
      control.setErrors(null);
    }
  }

  async loadMatchingDocument(question) {
    const documents = this.surveyProvider.documents$.value;

    if (documents && documents.length > 0) {
      const matchingDocument = documents.find((document) => document.questionId === question.id);

      if (matchingDocument) {
        const currentDocuments = this.matchingDocuments$.value;
        // Remover documento anterior si existe para esta pregunta
        const filteredDocuments = currentDocuments.filter((doc) => doc.question !== question.id);
        
        const newDocument = {
          question: question.id,
          document: matchingDocument.document
        };
        
        this.matchingDocuments$.next(
          filteredDocuments.concat(newDocument)
        );

        // Limpiar imagen si existe
        this.clearImage(question.id);

        return;
      }
    }
    return null;
  }

  getDocumentName(questionId: string) {
    const documents = this.matchingDocuments$.value;
    
    const matchingDocument = documents.find((document) => document.question === questionId);
    
    if (!matchingDocument) {
      return null;
    }

    const name = matchingDocument.document.name || matchingDocument.document.document_name;
    return name;
  }

  getDocumentSize(questionId: string) {
    const documents = this.matchingDocuments$.value;
    const matchingDocument = documents.find((document) => document.question === questionId);
    if (!matchingDocument) {
      return null;
    }

    const size = matchingDocument.document.blob?.size;
    if (!size) return null;

    const bytes = size;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  private clearImage(questionId: string) {
    const images = this.matchingImages$.value;
    const filteredImages = images.filter((image) => image.question !== questionId);
    this.matchingImages$.next(filteredImages);
  }

  private clearDocument(questionId: string) {
    const documents = this.matchingDocuments$.value;
    const filteredDocuments = documents.filter((document) => document.question !== questionId);
    this.matchingDocuments$.next(filteredDocuments);
  }
}
